/**
 * GameMap — structure-of-arrays storage for the tile grid. Typed arrays keep the
 * whole map in a handful of contiguous buffers (cache friendly, cheap to
 * serialise for save games). Enum values are stored as small integer codes with
 * lookup tables for conversion back to the readable domain enums.
 */
import { TerrainType, Biome, DepositType } from '@settlers/shared';
import type { Tile, Deposit } from '@settlers/shared';
import { asTileIndex } from '@settlers/shared';
import type { TileIndex } from '@settlers/shared';

/** Ordered code tables. Index === stored numeric code. Append-only to keep save
 * compatibility. */
export const TERRAIN_BY_CODE: readonly TerrainType[] = [
  TerrainType.DeepWater,
  TerrainType.ShallowWater,
  TerrainType.Sand,
  TerrainType.Grass,
  TerrainType.Forest,
  TerrainType.Dirt,
  TerrainType.Rock,
  TerrainType.Snow,
  TerrainType.Swamp,
];
export const BIOME_BY_CODE: readonly Biome[] = [
  Biome.Ocean,
  Biome.Lake,
  Biome.Beach,
  Biome.Meadow,
  Biome.Forest,
  Biome.Mountain,
  Biome.Snow,
  Biome.Swamp,
  Biome.Desert,
];
export const DEPOSIT_BY_CODE: readonly DepositType[] = [
  DepositType.None,
  DepositType.Forest,
  DepositType.StoneRock,
  DepositType.IronVein,
  DepositType.CoalSeam,
  DepositType.GoldVein,
  DepositType.FishShoal,
];

const codeOf = <T>(table: readonly T[], value: T): number => {
  const idx = table.indexOf(value);
  if (idx < 0) throw new Error(`Unknown enum value in code table: ${String(value)}`);
  return idx;
};

export const terrainCode = (t: TerrainType): number => codeOf(TERRAIN_BY_CODE, t);
export const biomeCode = (b: Biome): number => codeOf(BIOME_BY_CODE, b);
export const depositCode = (d: DepositType): number => codeOf(DEPOSIT_BY_CODE, d);

export interface GameMapBuffers {
  readonly terrain: Uint8Array;
  readonly biome: Uint8Array;
  readonly elevation: Uint8Array;
  readonly buildable: Uint8Array;
  readonly height: Float32Array;
  readonly moisture: Float32Array;
  readonly temperature: Float32Array;
  readonly moveCost: Float32Array;
  readonly depositType: Uint8Array;
  readonly depositAmount: Float32Array;
}

export class GameMap {
  readonly width: number;
  readonly height: number;
  readonly seed: number;
  readonly buffers: GameMapBuffers;

  constructor(width: number, mapHeight: number, seed: number, buffers?: GameMapBuffers) {
    this.width = width;
    this.height = mapHeight;
    this.seed = seed;
    const count = width * mapHeight;
    this.buffers = buffers ?? {
      terrain: new Uint8Array(count),
      biome: new Uint8Array(count),
      elevation: new Uint8Array(count),
      buildable: new Uint8Array(count),
      height: new Float32Array(count),
      moisture: new Float32Array(count),
      temperature: new Float32Array(count),
      moveCost: new Float32Array(count),
      depositType: new Uint8Array(count),
      depositAmount: new Float32Array(count),
    };
  }

  get tileCount(): number {
    return this.width * this.height;
  }

  inBounds(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.width && y < this.height;
  }

  index(x: number, y: number): TileIndex {
    return asTileIndex(y * this.width + x);
  }

  xOf(index: TileIndex): number {
    return index % this.width;
  }

  yOf(index: TileIndex): number {
    return Math.floor(index / this.width);
  }

  terrainAt(x: number, y: number): TerrainType {
    return TERRAIN_BY_CODE[this.buffers.terrain[this.index(x, y)] ?? 0] as TerrainType;
  }

  biomeAt(x: number, y: number): Biome {
    return BIOME_BY_CODE[this.buffers.biome[this.index(x, y)] ?? 0] as Biome;
  }

  elevationAt(x: number, y: number): number {
    return this.buffers.elevation[this.index(x, y)] ?? 0;
  }

  moveCostAt(x: number, y: number): number {
    return this.buffers.moveCost[this.index(x, y)] ?? Number.POSITIVE_INFINITY;
  }

  isBuildable(x: number, y: number): boolean {
    return (this.buffers.buildable[this.index(x, y)] ?? 0) === 1;
  }

  depositAt(x: number, y: number): Deposit {
    const i = this.index(x, y);
    return {
      type: DEPOSIT_BY_CODE[this.buffers.depositType[i] ?? 0] as DepositType,
      amount: this.buffers.depositAmount[i] ?? 0,
    };
  }

  /** Materialise a full Tile record (convenience — avoid in hot loops). */
  tileAt(x: number, y: number): Tile {
    const i = this.index(x, y);
    return {
      terrain: this.terrainAt(x, y),
      biome: this.biomeAt(x, y),
      height: this.buffers.height[i] ?? 0,
      moisture: this.buffers.moisture[i] ?? 0,
      temperature: this.buffers.temperature[i] ?? 0,
      elevation: this.elevationAt(x, y),
      buildable: this.isBuildable(x, y),
      moveCost: this.moveCostAt(x, y),
      deposit: this.depositAt(x, y),
    };
  }
}
